import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider
} from '@mui/material';

const IndividualCustomerReport = ({ data }) => {
  const { customer, summary, invoices, payments } = data;

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'success';
      case 'in progress': return 'warning';
      case 'closed': return 'primary';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Individual Customer Report
      </Typography>
      
      {/* Customer Information */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Customer Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Name:</strong> {customer.fullName}</Typography>
              <Typography><strong>Email:</strong> {customer.email || 'N/A'}</Typography>
              <Typography><strong>Mobile:</strong> {customer.mobile || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Home:</strong> {customer.home || 'N/A'}</Typography>
              <Typography><strong>Work:</strong> {customer.work || 'N/A'}</Typography>
              <Typography><strong>Status:</strong> 
                <Chip 
                  label={customer.status} 
                  color={customer.status === 'active' ? 'success' : 'error'} 
                  size="small" 
                  sx={{ ml: 1 }} 
                />
              </Typography>
            </Grid>
          </Grid>
          {customer.fullAddress && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography><strong>Address:</strong> {customer.fullAddress}</Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Invoices</Typography>
              <Typography variant="h5">{summary.totalInvoices}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Spent</Typography>
              <Typography variant="h5">{formatCurrency(summary.totalSpent)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Gross Collected</Typography>
              <Typography variant="h5">{formatCurrency(summary.grossCollected || summary.totalPaid)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Refunds</Typography>
              <Typography variant="h5" color="error.main">{formatCurrency(summary.totalRefunded)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Net Collected</Typography>
              <Typography variant="h5">{formatCurrency(summary.netCollected)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Outstanding Balance</Typography>
              <Typography variant="h5" color={summary.outstandingBalance > 0 ? 'error' : 'success'}>
                {formatCurrency(summary.outstandingBalance)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Invoices Table */}
      <Typography variant="h6" gutterBottom>Invoice History</Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Total Amount</TableCell>
              <TableCell align="right">Gross Collected</TableCell>
              <TableCell align="right">Refund Amount</TableCell>
              <TableCell align="right">Net Collected</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Location</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => {
              const refundAmount = (invoice.refunds || []).reduce((sum, refund) => {
                const status = String(refund.status || '').toLowerCase();
                if (status === 'approved' || status === 'processed') {
                  return sum + (parseFloat(refund.refundAmount) || 0);
                }
                return sum;
              }, 0);
              const grossCollected = parseFloat(invoice.paidAmount || 0);
              const netCollected = grossCollected - refundAmount;

              return (
              <TableRow key={invoice.id}>
                <TableCell>{invoice.invoiceNumber}</TableCell>
                <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                <TableCell align="right">{formatCurrency(invoice.totalAmount)}</TableCell>
                <TableCell align="right">{formatCurrency(grossCollected)}</TableCell>
                <TableCell align="right" sx={{ color: refundAmount > 0 ? 'error.main' : 'inherit' }}>
                  {formatCurrency(refundAmount)}
                </TableCell>
                <TableCell align="right">{formatCurrency(netCollected)}</TableCell>
                <TableCell align="right">
                  {formatCurrency(Math.max(0, parseFloat(invoice.totalAmount || 0) - grossCollected - refundAmount))}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={invoice.status} 
                    color={getStatusColor(invoice.status)} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>{invoice.location?.name || 'N/A'}</TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Payment History */}
      {payments && payments.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>Payment History</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Invoice #</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                    <TableCell>{payment.invoice?.invoiceNumber || 'N/A'}</TableCell>
                    <TableCell align="right">{formatCurrency(payment.paymentAmount)}</TableCell>
                    <TableCell>{payment.paymentMethod?.name || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={payment.status} 
                        color={getStatusColor(payment.status)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{payment.notes || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography variant="caption" color="textSecondary">
          Report generated on {new Date(data.generatedAt).toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default IndividualCustomerReport;
